import { useEffect, useRef } from "react";
import { useAppSelector } from "../../../App/hooks";
import useRequest from "../../../Hooks/useRequest";
import OrderBookTable from "../../../Tables/Order/OrderBookTable";
import InfoBox from "./InfoBox";
import { getCCOrderBook } from "src/Requests/ccOrder";
import { ICCOrderbookSearchParams, ICCOrderLeg } from "src/Interfaces/ccOrder";
import { OrderBook } from "src/Interfaces/orderInterfaces";
import useLiveOrderBooks from "src/Hooks/useLiveOrderBooks";

const OrderBooks = () => {
  const formValues = useAppSelector(
    (store) => store.ccOrder,
  ).addOrderFormValues;
  const buyLegs = formValues?.coveredCallOrderBuyLegs || ([] as ICCOrderLeg[]);
  const sellLeg = formValues?.coveredCallOrderSellLeg || ({} as ICCOrderLeg);
  const coveredCallPositionStrategyEnum =
    formValues?.coveredCallPositionStrategyEnum;
  const need3Ins = coveredCallPositionStrategyEnum === "8";

  function getValues() {
    const body: ICCOrderbookSearchParams = {
      instrumentIds: [],
      orderSides: [],
      coveredCallPositionStrategyEnum:
        formValues?.coveredCallPositionStrategyEnum
          ? Number(formValues.coveredCallPositionStrategyEnum)
          : null,
      orderType: Number(formValues?.coveredCallOrderType),
    };
    if (buyLegs && buyLegs.length)
      buyLegs.forEach((leg) => {
        if (leg.instrumentId) body.instrumentIds?.push(leg.instrumentId);
        if (leg.orderSide) body.orderSides?.push(leg.orderSide);
      });
    if (sellLeg && sellLeg.instrumentId)
      body.instrumentIds?.push(sellLeg.instrumentId);
    if (sellLeg && sellLeg.orderSide) body.orderSides?.push(sellLeg.orderSide);
    return body;
  }

  const orderBookRequest = useRequest(
    (abortSignal) => getCCOrderBook(getValues(), abortSignal),
    (e) => {},
  );
  const liveOrderBooks = useLiveOrderBooks({
    enabled: true,
    instrumentIds: [
      ...buyLegs.map((leg) => leg.instrumentId),
      sellLeg.instrumentId,
    ],
  });

  useEffect(() => {
    if (!need3Ins) {
      if (!buyLegs.length || !sellLeg || !sellLeg.instrumentId) return;
    } else if (buyLegs.length !== 2 || !sellLeg || !sellLeg.instrumentId)
      return;
    orderBookRequest.fetchData();
  }, [
    buyLegs,
    sellLeg,
    formValues?.coveredCallOrderType,
    formValues?.coveredCallPositionStrategyEnum,
  ]);

  useEffect(() => {
    orderBookRequest.resetResponse();
  }, [coveredCallPositionStrategyEnum]);

  useEffect(() => {
    if (!need3Ins) {
      if (!buyLegs.length || !sellLeg || !sellLeg.instrumentId) return;
    } else if (buyLegs.length !== 2 || !sellLeg || !sellLeg.instrumentId)
      return;

    const interval = setTimeout(() => {
      orderBookRequest.fetchData();
    }, 5000);
    return () => clearInterval(interval);
  }, [orderBookRequest.response, orderBookRequest.error]);

  const getOrderbookValues = (values?: OrderBook[]): OrderBook[] => {
    if (!values || !values.length)
      return new Array(need3Ins ? 3 : 2).fill(null as any);

    if (values.length < 2) {
      const res: OrderBook[] = [...values];
      for (let index = 0; index < 2 - values.length; index++) {
        res.push(null as any);
      }
      return res;
    }
    return values;
  };

  return (
    <div className="flex flex-col justify-start items-start">
      <div className="flex flex-row justify-start items-end pt-5">
        {getOrderbookValues(orderBookRequest?.response?.orderBooks)?.map(
          (orderbook) => (
            <div className="mx-0.5">
              <OrderBookTable
                tableInfo={liveOrderBooks.resolveOrderBook(orderbook)}
              />
            </div>
          ),
        )}
      </div>
      <InfoBox
        info={
          orderBookRequest.response && orderBookRequest.response.orderBooks
            ? orderBookRequest?.response
            : null
        }
      />
    </div>
  );
};

export default OrderBooks;
